import { ID, OptionDsisplayType } from '../../common/ui/ui-types';

export const DATA_LOADING_TIME_IN_MS = 300;
export const DEFAULT_LABEL_FONT_SIZE = 16;
export const DEFAULT_LABEL_COLOR = '#000000';

export type ProductCustomizationOptionType = 'custom-print-template' | 'custom-message' | 'custom-options' | 'custom-text-frames';
export type BackgroundImageDisplayType = 'name-list' | 'image-tiles';

export interface DisplayableCustomizableAsset {
    id: string;
    type: string;
    name: string;
    text: string;
    previewUrl?: string;
    downloadUrl?: string;
    content?: string;
    required?: boolean;
}

export interface DisplayTextFrameGroup {
    id: string;
    groupName: string;
    /**
     * Owning image-frame name for nested overlays. Absent for standalone
     * (`custom-text-frames`) overlays and historical orders.
     */
    imageFrameName?: string;
    /**
     * Composed display string: "Group Name (Frame Name)" when nested, bare
     * "Group Name" when standalone. Renderers prefer this and fall back to
     * `groupName` when absent.
     */
    displayName: string;
    fontFamily: string;
    color: string;
    colorName: string;
    frames: { name: string; content: string }[];
}

export interface DisplayPaidOption {
    id: string;
    name: string;
    key?: string;
    content: string;
    amount: number;
    required?: boolean;
}

export interface CustomizableImageDownloadResult {
    imageDownloadUrl: string;
    thumbnailImageDownloadUrl: string;
}


/**
 * Admin-UI mirror of the storecore `ImageFrameData` shape.
 *
 * `textFrameGroups` holds image-frame-embedded text overlays whose drag/resize
 * boundary is this frame's rect rather than the full canvas. Nested per-frame is
 * the only supported location for overlay groups on print templates.
 */
export interface ImageFrameData {
    name: string;
    label?: string;
    labelColor?: string;
    labelFontSize?: number;
    positionX: number;
    positionY: number;
    sizeX: number;
    sizeY: number;
    textFrameGroups?: TextFrameGroupConfig[];
}

export interface CustomizationMessageData {
    label: string;
    description: string;
    required: boolean;
}

export interface PaidOptionData {
    label: string;
    description: string;
    required: boolean;
    showImage: boolean;
    displayType: OptionDsisplayType;
    options: PaidOptionItem[];
}

export interface PaidOptionItem {
    key: string;
    label: string;
    description: string;
    assetId: ID | null;
    price: number;
}

export interface LabeledImageData {
    label: string;
    assetId: ID | null;
}

export interface TextFrameData {
    name: string;
    label?: string;
    maxLength: number;
    maxLines?: number;
    required: boolean;
    textAlign: 'left' | 'center' | 'right';
}

export interface TextFrameColorOption {
    hex: string;
    name: string;
}

export function normalizeTextFrameColor(c: string | TextFrameColorOption): TextFrameColorOption {
    if (typeof c === 'string') return { hex: c, name: '' };
    // Coerce object colors from arbitrary imported JSON so hex/name are always
    // present (mirrors the server-side normalizer in customizable-product/types.ts).
    return { hex: String((c as any)?.hex ?? ''), name: String((c as any)?.name ?? '') };
}

export interface TextFrameGroupConfig {
    name: string;
    allowedFonts: string[];
    allowedColors: TextFrameColorOption[];
    defaultFont: string;
    defaultColor: string;
    autoFit: boolean;
    fontSizeOptions: number[];
    defaultFontSize?: number;
    frames: TextFrameData[];
}

export interface CustomizedPicturePatternData {
    label: string;
    description: string;
    minWidth: number;
    minHeight: number;
    canvasWidth: number;
    canvasHeight: number;
    backgroundImageDisplayType: BackgroundImageDisplayType;
    backgroundImages: LabeledImageData[];
    imageFrames: ImageFrameData[];
}

export interface CustomTextFramesContent {
    label: string;
    description: string;
    textFrameGroups: TextFrameGroupConfig[];
}
